import Tippy, { useSingleton } from "@tippyjs/react";
import { useAtomValue } from "jotai";
import { useRef } from "react";
import { canv } from "@/canv/global";
import { MakeRequest } from "@/canv/utils";
import { ExportIcon, FavouriteIcon, MenuIcon } from "@/icons";
import { boardInfoAtom, buttonDisabledAtom, headerActiveButtonAtom } from "@/interface/AppStore";
import { BottomBarItem } from "@/interface/BottomBar";
import { Tooltip } from "@/interface/ui/Tooltip";
import { Button } from "@/shared/ui/Button";
import styles from "./LeftBar.module.css";
import { LogoLink } from "./LogoLink";

type LeftBarProps = {
    className?: string;
};

export const LeftBar = ({ className }: LeftBarProps) => {
    const [source, target] = useSingleton();
    const {
        boardName,
        canUseFavourite,
        isFavourite,
        isProfessional,
        isTeamOwner,
        boardMode,
        teamId,
    } = useAtomValue(boardInfoAtom);
    const openSidebarName = useAtomValue(headerActiveButtonAtom);
    const buttonDisabled = useAtomValue(buttonDisabledAtom);
    const setFavouriteLoadingRef = useRef(false);

    const showTariff = !isProfessional && isTeamOwner === true;
    const showExport = !(boardMode === "view" || boardMode === "view_mini");

    const handleFavouriteClick = () => {
        if (setFavouriteLoadingRef.current) {
            return;
        }
        setFavouriteLoadingRef.current = true;
        MakeRequest(
            "/api/v2/board/meta/favourite/set",
            {
                boardUID: canv.getInitDataProp("boardUID"),
                token: canv.getInitDataProp("userToken"),
                isFavourite: !isFavourite,
            },
            true,
        )
            .then(() => {
                canv.setInitDataProp("isFavourite", !isFavourite);
            })
            .catch((e) => {
                console.log(e);
                canv.captureException(e);
            })
            .finally(() => {
                setFavouriteLoadingRef.current = false;
            });
    };

    const handleUpgradeTariffButtonClick = () => {
        // TODO: сделать сразу ссылкой?
        if (!teamId) return;
        window.open(`${canv.host}/dashboard?need_tariff_payment=true&teamId=${teamId}`, "_blank");
    };

    return (
        <div className={className}>
            <Tippy singleton={source} />
            <LogoLink singletoneTarget={target} />
            <div className={styles.actions}>
                <Tooltip singleton={target} content="Переименовать">
                    <BottomBarItem
                        className={styles.boardName}
                        onClick={() => {
                            // TODO: переделать на реакт
                            if (canv.isGrantedAccess() || !canv.isBoardModeEditOrOwner()) {
                                return;
                            }
                            canv.appInterface.renameWindow.open();
                        }}
                    >
                        {boardName}
                    </BottomBarItem>
                </Tooltip>
                {canUseFavourite && (
                    <Tooltip
                        singleton={target}
                        content={isFavourite ? "Удалить из избранного" : "Добавить в избранное"}
                    >
                        <BottomBarItem onClick={handleFavouriteClick}>
                            <FavouriteIcon className={isFavourite ? styles.favourite : undefined} />
                        </BottomBarItem>
                    </Tooltip>
                )}
                {showExport && (
                    <Tooltip singleton={target} content="Экспорт">
                        <BottomBarItem
                            className="barPanelExport stroke"
                            active={openSidebarName === "export"}
                            disabled={buttonDisabled.export ?? false}
                            onClick={() => {
                                // TODO: переделать на реакт
                                canv.appInterface.exportWindow.open();
                            }}
                        >
                            <ExportIcon />
                        </BottomBarItem>
                    </Tooltip>
                )}
                {/* TODO: context menu */}
                <Tooltip singleton={target} content="Меню">
                    <BottomBarItem
                        className="barPanelSettings"
                        active={openSidebarName === "settings"}
                        onClick={() => {
                            // TODO: переделать на реакт
                            canv.appInterface.settingsWindow.show();
                        }}
                    >
                        <MenuIcon />
                    </BottomBarItem>
                </Tooltip>
            </div>
            {showTariff && (
                <Button variant="secondary" onClick={handleUpgradeTariffButtonClick}>
                    Улучшить тариф
                </Button>
            )}
        </div>
    );
};
